import React, { useState } from 'react';
import { Link } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { PreviewDisclaimer } from '../components/PreviewDisclaimer';
import { IconWrap } from '../components/IconWrap';
import { CiSearch, CiBullhorn, CiCircleChevRight } from 'react-icons/ci';
import { news } from './Home/data/news';
import useTheme from '../hooks/useTheme';

export const News: React.FC = () => {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const tags = Array.from(new Set(news.map((item) => item.tag)));
  const pinnedNews = news.find((item) => item.tag === 'Announcement') || news[0];

  const filteredNews = news.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = selectedTag === null || item.tag === selectedTag;
    return matchesSearch && matchesTag && item.id !== pinnedNews.id;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getTagIcon = (tag: string) => {
    switch (tag) {
      case 'Announcement':
        return <CiBullhorn />;
      default:
        return null;
    }
  };

  return (
    <>
      <PageHero
        title="News & Updates"
        subtitle="Latest news and updates from BCBH."
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'News' }]}
      />

      <Section>
        <PreviewDisclaimer />

        {/* Pinned Featured Card */}
        <Card
          hoverable
          style={{
            marginBottom: '32px',
            border: `2px solid ${theme.colors.green.g1}`,
            backgroundColor: theme.background.panel,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px',
            }}
          >
            <IconWrap icon={<CiBullhorn />} size={20} color="primary" />
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: theme.colors.green.g1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Pinned: {pinnedNews.tag}
            </span>
            <span
              style={{
                fontSize: '12px',
                color: theme.text.muted,
              }}
            >
              {formatDate(pinnedNews.date)}
            </span>
          </div>
          <h3
            style={{
              fontSize: '24px',
              fontWeight: 600,
              color: theme.text.primary,
              marginBottom: '12px',
            }}
          >
            {pinnedNews.title}
          </h3>
          <p
            style={{
              color: theme.text.muted,
              fontSize: '16px',
              lineHeight: 1.6,
              marginBottom: '16px',
            }}
          >
            {pinnedNews.excerpt}
          </p>
          <Link
            to={`/news/${pinnedNews.slug}`}
            style={{
              color: theme.colors.green.g1,
              textDecoration: 'none',
              fontSize: '15px',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
            className="learnMoreLink"
          >
            Read more
            <IconWrap icon={<CiCircleChevRight />} size={16} color="primary" />
          </Link>
        </Card>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '24px',
            marginBottom: '32px',
          }}
          className="newsFilters"
        >
          <div style={{ position: 'relative' }}>
            <IconWrap
              icon={<CiSearch />}
              size={18}
              color="muted"
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
            <input
              type="text"
              placeholder="Search news..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '12px 16px 12px 44px',
                backgroundColor: theme.background.card,
                border: `1px solid ${theme.colors.line}`,
                borderRadius: theme.borderRadius.r1,
                color: theme.text.primary,
                fontSize: '16px',
                fontFamily: 'inherit',
                width: '100%',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <button
              onClick={() => setSelectedTag(null)}
              style={{
                padding: '8px 16px',
                backgroundColor: selectedTag === null ? theme.colors.green.g1 : theme.background.card,
                color: selectedTag === null ? theme.text.primary : theme.text.muted,
                border: `1px solid ${selectedTag === null ? theme.colors.green.g1 : theme.colors.line}`,
                borderRadius: theme.borderRadius.r1,
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: selectedTag === null ? `0 0 0 2px ${theme.colors.green.g1}40` : 'none',
              }}
            >
              All
            </button>
            {tags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: selectedTag === tag ? theme.colors.green.g1 : theme.background.card,
                  color: selectedTag === tag ? theme.text.primary : theme.text.muted,
                  border: `1px solid ${selectedTag === tag ? theme.colors.green.g1 : theme.colors.line}`,
                  borderRadius: theme.borderRadius.r1,
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: selectedTag === tag ? `0 0 0 2px ${theme.colors.green.g1}40` : 'none',
                }}
              >
                {getTagIcon(tag) && <IconWrap icon={getTagIcon(tag)} size={14} color={selectedTag === tag ? 'default' : 'muted'} />}
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '24px',
          }}
          className="newsListGrid"
        >
          {filteredNews.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.slug}`}
              style={{ textDecoration: 'none' }}
            >
              <Card hoverable style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: theme.colors.green.g1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {item.tag}
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      color: theme.text.muted,
                    }}
                  >
                    <IconWrap icon={<CiBullhorn />} size={12} color="muted" />
                    {formatDate(item.date)}
                  </div>
                </div>
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: theme.text.primary,
                    marginBottom: '12px',
                    lineHeight: 1.35,
                  }}
                >
                  {item.title}
                </h3>
                <p
                  style={{
                    color: theme.text.muted,
                    fontSize: '14px',
                    lineHeight: 1.6,
                    flexGrow: 1,
                    marginBottom: '16px',
                  }}
                >
                  {item.excerpt}
                </p>
                <div
                  style={{
                    paddingTop: '16px',
                    borderTop: `1px solid ${theme.colors.line}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: theme.colors.green.g1,
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Read more
                  <IconWrap icon={<CiCircleChevRight />} size={16} color="primary" />
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {filteredNews.length === 0 && (
          <Card>
            <p style={{ color: theme.text.muted, textAlign: 'center' }}>
              No news found matching your criteria.
            </p>
          </Card>
        )}
      </Section>
    </>
  );
};
